// Zoho exposes no public link for a sales order, so the PDF is streamed
// through our backend. Shared by the recurring-sub detail and the domain
// detail so both name the saved file identically.

// Pull the server-chosen filename (minus .pdf) out of Content-Disposition, so
// the saved file matches the sales-order number the backend resolved.
const filenameFromDisposition = (resp) => {
  const raw = resp.headers.get("Content-Disposition") || "";
  const m = raw.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const name = m ? decodeURIComponent(m[1]).trim() : "";
  return name.replace(/\.pdf$/i, "");
};

// Read the backend's JSON { msg } off a failed response without letting a
// non-JSON body (an nginx error page, say) mask the status.
const errorFromResponse = async (resp) => {
  try {
    const body = await resp.json();
    if (body?.msg) return body.msg;
  } catch (_) {
    /* not JSON */
  }
  return `Server returned ${resp.status}.`;
};

/**
 * Fetch a Zoho sales-order PDF through our backend and either show it or
 * save it.
 *
 * @param soId        Zoho salesorder_id
 * @param disposition "inline" to view, "attachment" to download
 * @param soNumber    human-readable ETPL-SO… number, used as a filename fallback
 * @param onError     called with a human-readable string when anything fails
 * @returns true on success, false otherwise
 */
export const openAdminSalesOrder = async (
  soId,
  disposition,
  soNumber,
  onError,
) => {
  const fail = (msg) => {
    if (typeof onError === "function") onError(msg);
    else console.error("[salesOrderPdf]", msg);
    return false;
  };

  if (!soId) return fail("This bill has no Zoho sales-order id.");

  // POPUP BLOCKER. A window.open() that runs AFTER an await is no longer
  // attributable to the click that started it, so Chrome blocks it outright
  // and the View button appears to do nothing at all. Claim the tab
  // synchronously, while we are still inside the click handler, and point it
  // at the blob once the fetch lands.
  let viewer = null;
  if (disposition !== "attachment") {
    viewer = window.open("", "_blank");
    if (viewer) {
      // Something to look at while the PDF streams, instead of about:blank.
      try {
        viewer.document.write(
          "<title>Loading sales order…</title><p style=\"font:14px system-ui;padding:24px\">Fetching the sales order from Zoho…</p>",
        );
      } catch (_) {
        /* cross-origin-ish edge cases — harmless */
      }
    }
  }

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/admin/sales-order/${soId}/pdf?disposition=${disposition}`,
      { credentials: "include" },
    );

    if (!resp.ok) {
      if (viewer) viewer.close();
      return fail(await errorFromResponse(resp));
    }

    const blob = await resp.blob();
    // A JSON error body that slipped through with a 200 would land here as a
    // tiny non-PDF blob. Saving it as .pdf gives a file that will not open,
    // which reads to the user as another silent failure.
    if (!blob.size) {
      if (viewer) viewer.close();
      return fail("Zoho returned an empty file.");
    }

    const url = URL.createObjectURL(blob);
    if (disposition === "attachment") {
      const a = document.createElement("a");
      a.href = url;
      // Save as the sales-order NUMBER (ETPL-SO2627-549.pdf), never the Zoho id.
      a.download = `${
        filenameFromDisposition(resp) || soNumber || `sales-order-${soId}`
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else if (viewer) {
      viewer.location.href = url;
    } else {
      // The tab was blocked despite being opened on the click. Download it
      // instead of failing — the admin still gets the document.
      const a = document.createElement("a");
      a.href = url;
      a.download = `${
        filenameFromDisposition(resp) || soNumber || `sales-order-${soId}`
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (typeof onError === "function") {
        onError(
          "Your browser blocked the new tab, so the sales order was downloaded instead.",
        );
      }
    }
    // Long enough for the viewer tab to finish loading the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch (e) {
    if (viewer) viewer.close();
    return fail(e?.message || "Could not reach the server.");
  }
};

export default openAdminSalesOrder;
